import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch} from "react-redux";
import MindsetsJsSDK, {
    filterConstants,
    ChallengeDropdown,
    ErrorMessageAction,
    InfoMessageAction,
    LoadingAction, QuestionDropdown
} from "mindsets-js-sdk";

import PracticeSkillDropdown from "../../common/PracticeSkillDropdown";

export default function () {
    const dispatch = useDispatch();
    const [challenge_code, setChallengeCode] = useState(null);
    const [question_code, setQuestionCode] = useState(null);
    const [practice_skill, setPracticeSkill] = useState(null);
    const [number_responses, setNumberResponses] = useState(3);
    const [grading_instructions, setGradingInstructions] = useState(['', '']);
    const [response, setResponse] = useState('');
    const [results, setResults] = useState(null);
    const [instructions_quality, setInstructionsQuality] = useState('');
    const [instructions_suggestions, setInstructionsSuggestions] = useState('');
    const [example, setExample] = useState('');
    const [offset, setOffset] = useState(0);

    const debounced = useCallback(_.debounce(a => a(), 600), []);

    useEffect(() => {
        setQuestionCode(null);
        setResults(null);
        setGradingInstructions(['', '']);
    }, [challenge_code]);

    useEffect(() => {
        setGradingInstructions(['', '']);
        setResults(null);
    }, [question_code]);

    useEffect(() => {
        setResults(null);
    }, [practice_skill]);

    useEffect(() => {
        return () => {
            setChallengeCode(null);
            setQuestionCode(null);
            setGradingInstructions(['', '']);
            setResults(null);
            setPracticeSkill(null);
            setNumberResponses(3);
            setInstructionsQuality('');
            setInstructionsSuggestions('');
            setExample('');
            setOffset(0);
        }
    }, []);

    function renderTestResults() {
        if (results === null) {
            return null;
        } else if (Array.isArray(results) && results.length > 0) {
            return (
                <table className='table table-bordered table-sm mt-3'>
                    <thead>
                    <tr>
                        <th>Response</th>
                        <th>AI reply</th>
                    </tr>
                    </thead>
                    <tbody>
                    {results.map((result, i) => {
                        return (
                            <tr key={i}>
                                <td><div dangerouslySetInnerHTML={{__html: result.response}}/></td>
                                <td>{result.reply}</td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            );
        }

        return (
            <div>No results</div>
        );
    }

    function renderTestButton() {
        return (
            <div>
                <div className='d-flex flex-gap-10'>
                    <div>
                        <label>Select the number of the most recent responses to test on</label>
                        <select value={number_responses} onChange={event => {
                            setNumberResponses(event.target.value);
                            setOffset(0);
                        }}>
                            <option value={1}>1 response</option>
                            <option value={3}>3 responses</option>
                            <option value={5}>5 responses</option>
                            <option value={7}>7 responses</option>
                            <option value={10}>10 responses</option>
                        </select>
                    </div>
                    <div>OR</div>
                    <div>
                        <label>Provide your own response example</label>
                        <textarea
                            rows={5}
                            value={response}
                            onChange={event => setResponse(event.target.value)}/>
                    </div>
                </div>
                <button
                    onClick={event => {
                        event.preventDefault();
                        dispatch(LoadingAction(true, 'Loading ...'));
                        MindsetsJsSDK().API.post('ai/test_grading_instructions', {
                            challenge_code,
                            question_code,
                            practice_skill_id: practice_skill.id,
                            number_responses,
                            offset,
                            grading_instructions,
                            response
                        }).then(response => {
                            setResults(response.items);
                            setOffset(offset + number_responses);
                            dispatch(LoadingAction(false));
                        }).catch(error => {
                            dispatch(LoadingAction(false));
                            dispatch(ErrorMessageAction(error));
                        });
                    }}
                    disabled={grading_instructions.filter(i => i.length > 0).length !== grading_instructions.length}
                >Test {offset > 0 ? ' next ' + number_responses : ''}
                </button>
            </div>
        );
    }

    function renderHelpButton() {
        if (question_code && practice_skill) {
            return (
                <div>
                    <button
                        onClick={event => {
                            event.preventDefault();
                            dispatch(LoadingAction(true, 'Loading ...'));
                            MindsetsJsSDK().API.post('ai/help_grading_instructions', {
                                challenge_code,
                                question_code,
                                practice_skill_id: practice_skill.id,
                                max_points: grading_instructions.length - 1
                            }).then(response => {
                                setGradingInstructions(response.items);
                                setResults(null);
                                dispatch(LoadingAction(false));
                            }).catch(error => {
                                dispatch(LoadingAction(false));
                                dispatch(ErrorMessageAction(error));
                            });
                        }}
                    >Ask AI to help me
                    </button>
                </div>
            );
        }

        return null;
    }

    function renderGenerateButton() {
        if (question_code && practice_skill) {
            return (
                <div>
                    <button
                        onClick={event => {
                            event.preventDefault();
                            const instruction0 = 'the response does not demonstrate that "' + practice_skill.description + '".';
                            const instruction1 = 'the response partially demonstrates that "' + practice_skill.description + '".';
                            const instruction2 = 'the response fully demonstrates that "' + practice_skill.description + '".';
                            setGradingInstructions([instruction0, instruction1, instruction2]);
                        }}
                    >Generate from the selected Skill
                    </button>
                </div>
            );
        }

        return null;
    }

    function renderLoadButton() {
        if (question_code && practice_skill) {
            return (
                <div>
                    <button
                        onClick={() => {
                            dispatch(LoadingAction(true, 'Loading ...'));
                            MindsetsJsSDK().API.get('question_practice_skills', {
                                'filters[question_code][0]': filterConstants.EXPRESSION_EQUAL + '=' + question_code,
                                'filters[practice_skill_id][0]': filterConstants.EXPRESSION_EQUAL + '=' + practice_skill.id,
                                'filters[mode][0]': filterConstants.EXPRESSION_EQUAL + '=ai'
                            }).then(response => {
                                if (response.items.length > 0) {
                                    setGradingInstructions(response.items[0].grading_instructions);
                                    setResults(null);
                                } else {
                                    dispatch(InfoMessageAction('Data for selected Skill does not exist. Try another Skill.'));
                                }
                                dispatch(LoadingAction(false));
                            }).catch(error => {
                                dispatch(LoadingAction(false));
                                dispatch(ErrorMessageAction(error));
                            });
                        }}
                        disabled={!question_code || !practice_skill}
                    >Load from existing rubric
                    </button>
                </div>
            );
        }

        return null;
    }

    function renderGoodButton() {
        if (question_code && practice_skill) {
            return (
                <div>
                    <button
                        onClick={() => {
                            dispatch(LoadingAction(true, 'Loading ...'));
                            MindsetsJsSDK().API.post('ai/verify_instructions', {
                                challenge_code,
                                question_code,
                                practice_skill_id: practice_skill.id,
                                grading_instructions
                            }).then(response => {
                                setInstructionsQuality(response.reply.quality);
                                setInstructionsSuggestions(response.reply.suggestions);
                                setExample(response.reply.example);
                            }).catch(error => dispatch(ErrorMessageAction(error))).then(() => dispatch(LoadingAction(false)));
                        }}
                    >Double-check with AI
                    </button>
                </div>
            );
        }

        return null;
    }

    function renderRubricForm() {
        return (
            <div className='mb-3 mt-3'>
                <div className='mb-3'>
                    <label>Max points</label>
                    <select
                        value={grading_instructions.length - 1}
                        onChange={event => {
                            event.preventDefault();
                            const grading_instructions_x = JSON.parse(JSON.stringify(grading_instructions));
                            const max_points = parseInt(event.target.value);

                            if (grading_instructions_x.length - 1 < max_points) {
                                let point = grading_instructions_x.length;
                                do {
                                    grading_instructions_x.push('');
                                    point++;
                                } while (grading_instructions_x.length - 1 < max_points);
                            } else if (grading_instructions_x.length - 1 > max_points) {
                                grading_instructions_x.splice(-((grading_instructions_x.length - 1) - max_points));
                            }
                            setGradingInstructions(grading_instructions_x);
                            setInstructionsQuality('');
                            setInstructionsSuggestions('');
                            setExample('');
                            setOffset(0);
                        }}
                    >
                        <option value={0} disabled={true}>Select max points</option>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                    </select>
                </div>
                <div className='mb-3'>
                    {grading_instructions.map((instruction, index) => {
                        return (
                            <div key={'instruction_' + index}>
                                <div className='d-flex flex-row align-items-center flex-gap-5'>
                                    <h3><label className="col-form-label">{index}</label></h3>
                                    <textarea
                                        className="form-control flex-1"
                                        value={instruction ? instruction : ''}
                                        onChange={event => {
                                            const grading_instructions_x = JSON.parse(JSON.stringify(grading_instructions));
                                            grading_instructions_x[index] = event.target.value;
                                            setGradingInstructions(grading_instructions_x);
                                            setInstructionsQuality('');
                                            setInstructionsSuggestions('');
                                            setExample('');
                                            setOffset(0);
                                        }}
                                        placeholder="Instructions"
                                        rows={5}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    function renderInstructionsQuality() {
        if (instructions_quality && instructions_suggestions && example) {
            return (
                <div>
                    <p>AI believes that the quality of these grading instructions is {instructions_quality}%</p>
                    <p>Here are suggestions from AI for improvements</p>
                    <p>{instructions_suggestions}</p>
                    <p>Here is suggested example:</p>
                    <p>{example}</p>
                </div>
            );
        }

        return null;
    }

    function renderBuilder() {
        if (challenge_code && question_code && practice_skill) {
            return (
                <div className='d-flex flex-gap-10 mb-3'>
                    <div className='bordered'>
                        <h3>Build</h3>
                        <div className='d-flex flex-gap-10 flex-wrap mt-3'>
                            {renderLoadButton()}
                            {renderHelpButton()}
                            {/*{renderGenerateButton()}*/}
                        </div>
                        {renderRubricForm()}
                    </div>
                    <div className='bordered'>
                        <h3>Verify with AI</h3>
                        {renderGoodButton()}
                        {renderInstructionsQuality()}
                    </div>
                </div>
            );
        }

        return null;
    }

    function renderTest() {
        if (challenge_code && question_code && practice_skill) {
            return (
                <div className='bordered'>
                    <h3>Test</h3>
                    {renderTestButton()}
                    {renderTestResults()}
                </div>
            );
        }

        return null;
    }

    return (
        <>
            <title>Rubric Composer</title>
            <h1>Rubric Composer</h1>

            <div className='section'>
                <div className='d-flex flex-gap-10 flex-wrap mb-3'>
                    <div>
                        <label>Select Challenge</label>
                        <ChallengeDropdown
                            onSelect={challenge_version => {
                                if (challenge_version) {
                                    setChallengeCode(challenge_version.challenge_code.code);
                                    setQuestionCode(null);
                                    setPracticeSkill(null);
                                    setGradingInstructions(['', '']);
                                    setResults(null);
                                    setInstructionsQuality('');
                                    setInstructionsSuggestions('');
                                    setExample('');
                                    setOffset(0);
                                } else {
                                    setChallengeCode(null);
                                    setQuestionCode(null);
                                    setPracticeSkill(null);
                                    setGradingInstructions(['', '']);
                                    setResults(null);
                                    setInstructionsQuality('');
                                    setInstructionsSuggestions('');
                                    setExample('');
                                    setOffset(0);
                                }

                                return challenge_version ? challenge_version.name : '';
                            }}
                            id='grading-instructions-composer-challenge-code'
                            with_reset={true}
                            onReset={() => {
                                setChallengeCode(null);
                                setQuestionCode(null);
                                setPracticeSkill(null);
                                setGradingInstructions(['', '']);
                                setResults(null);
                                setInstructionsQuality('');
                                setInstructionsSuggestions('');
                                setExample('');
                                setOffset(0);
                            }}
                            debounce={debounced}
                            unversioned={false}
                            show_counts={true}
                        />
                    </div>

                    <div>
                        <label>Select a question(you can type just "Q5")</label>
                        <QuestionDropdown
                            onSelect={question => {
                                if (question) {
                                    setQuestionCode(question.code);
                                } else {
                                    setQuestionCode(null);
                                }
                                setPracticeSkill(null);
                                setGradingInstructions(['', '']);
                                setResults(null);
                                setInstructionsQuality('');
                                setInstructionsSuggestions('');
                                setExample('');
                                setOffset(0);

                                return question ? ('Q' + question.number + ' ' + question.title) : '';
                            }}
                            id='rubric-composer-question'
                            challenge_code={challenge_code}
                            debounce={debounced}
                            show_counts={true}
                            placeholder='Title, code or number'
                            disabled={!challenge_code}
                            onReset={() => {
                                setQuestionCode(null);
                                setPracticeSkill(null);
                                setGradingInstructions(['', '']);
                                setResults(null);
                                setInstructionsQuality('');
                                setInstructionsSuggestions('');
                                setExample('');
                                setOffset(0);
                            }}
                        />
                    </div>

                    <div>
                        <label>Select a Skill</label>
                        <PracticeSkillDropdown
                            onSelect={skill => {
                                if (skill) {
                                    setPracticeSkill(skill);
                                    setGradingInstructions(['', '']);
                                    setResults(null);
                                    setInstructionsQuality('');
                                    setInstructionsSuggestions('');
                                    setExample('');
                                    setOffset(0);
                                } else {
                                    setPracticeSkill(null);
                                    setGradingInstructions(['', '']);
                                    setResults(null);
                                    setInstructionsQuality('');
                                    setInstructionsSuggestions('');
                                    setExample('');
                                    setOffset(0);
                                }

                                return skill ? skill.code + ' ' + skill.name : '';
                            }}
                            id='grading-instructions-composer-skill'
                            with_reset={true}
                            onReset={() => {
                                setPracticeSkill(null);
                                setGradingInstructions(['', '']);
                                setResults(null);
                                setInstructionsQuality('');
                                setInstructionsSuggestions('');
                                setExample('');
                                setOffset(0);
                            }}
                            debounce={debounced}
                            show_counts={true}
                            disabled={!challenge_code || !question_code}
                        />
                    </div>
                </div>
                {renderBuilder()}
                {renderTest()}
            </div>
        </>
    );
}
